import { createSelector } from '@reduxjs/toolkit';
import { CommonPagesI } from 'store/models/file-selected/CommonPages';
import {
  EachFileSelectedWithThumbnail,
  EachPageWithThumbnail,
} from 'store/models/file-selected/file-selected.interface';
import {
  curBookingsSelector,
  getViewingBookingSelector,
} from './cur-bookings.selector';
import { availableOnlyThumbnailMapSelector } from './ticket-attachment.selector';
import {
  ticketPreviewResultSelector,
  ticketIsLoadingPreviewBookingsSelector,
} from './ticket.selector';

export const commonPagesSelector = createSelector(
  curBookingsSelector,
  (storeState): CommonPagesI => storeState.commonPages,
);

export const commonPagesWithThumbnailSelector = createSelector(
  commonPagesSelector,
  availableOnlyThumbnailMapSelector,
  (commonPages, thumbnailMap): EachPageWithThumbnail[] => {
    if (!commonPages) return [];
    if (!thumbnailMap) throw Error(`need thumbnailMap`);
    return commonPages
      .map((p, i) => {
        const thumbnail =
          thumbnailMap[p.partId] && thumbnailMap[p.partId][p.pageNo];
        if (!thumbnail) return undefined;
        return {
          ...p,
          thumbnail,
        };
      })
      .filter((p) => p !== undefined) as EachPageWithThumbnail[];
  },
);
export const getCurFileSelectedSelector = createSelector(
  getViewingBookingSelector,
  (booking): EachFileSelectedWithThumbnail[] => {
    if (!booking) return [];
    // TODO: filter only those that are not in recycle
    const preFilter = booking.fileSelected as EachFileSelectedWithThumbnail[];
    console.debug('preFilter', preFilter);
    // const available =
    return preFilter;
  },
);

export const isLoadingPreviewBookingsAtViewingBookingSelector = createSelector(
  getViewingBookingSelector,
  ticketIsLoadingPreviewBookingsSelector,
  (viewingBooking, isLoadingPreviewBookings): boolean => {
    if (!viewingBooking) return false;
    const bookingNo = viewingBooking.bookingNo;
    return isLoadingPreviewBookings[bookingNo] ?? false;
  },
);

/**
 * with common pages
 * with thumnail
 */
export const fileSelectedWithThumbnailSelector = createSelector(
  getCurFileSelectedSelector,
  availableOnlyThumbnailMapSelector,
  ticketPreviewResultSelector,
  getViewingBookingSelector,
  (
    curBkFileSelected,
    thumbnailMap,
    ticketPreviewResult,
    viewingBooking,
  ): EachFileSelectedWithThumbnail[] => {
    const bookingNo = viewingBooking?.bookingNo;
    const fileSelected = curBkFileSelected;
    if (!fileSelected) return [];
    if (!thumbnailMap) throw Error(`need thumbnailMap`);
    const previewFilesAtBooking =
      (bookingNo && ticketPreviewResult[bookingNo]) ?? [];
    const previewFilesAtBookingN = previewFilesAtBooking.length;
    return fileSelected.map((newFile, i) => {
      const previewedFile =
        i < previewFilesAtBookingN ? previewFilesAtBooking[i] : {};
      const newPages = newFile.pages
        .map((p) => {
          const thumbnail =
            thumbnailMap[p.partId] &&
            thumbnailMap[p.partId][p.pageNo];
          if (!thumbnail) return undefined;
          return {
            ...p,
            thumbnail,
          };
        })
        .filter((p) => p !== undefined) as EachPageWithThumbnail[];
      return {
        ...newFile,
        previewedFile,
        pages: newPages,
      };
    });
  },
);

export const isSelectedDestFileSelectedIndexes = createSelector(
  getCurFileSelectedSelector,
  (curBkFileSelected): number[] => {
    const fileSelected = curBkFileSelected;
    if (!fileSelected) return [];
    return fileSelected
      .map((f, i) => (f.isSelected ? i : undefined))
      .filter((f) => f !== undefined) as number[];
  },
);

/**
 * if no selected, check the last file
 */
export const hasAnyAddableFileSelectedSelector = createSelector(
  getCurFileSelectedSelector,
  (curBkFileSelected): boolean => {
    const fileSelected = curBkFileSelected;
    if (!fileSelected) return false;
    return (
      fileSelected.findIndex(
        (f, i) => f.isSelected && f.canSelectPage,
      ) > -1 ||
      (fileSelected.length > 0 &&
        fileSelected[fileSelected.length - 1].canSelectPage) // or last index can be selected
    );
  },
);

/**
 * only selected and addable (can select page)
 */
export const hasAnyTrueAddableFileSelectedSelector = createSelector(
  getCurFileSelectedSelector,
  (curBkFileSelected): boolean => {
    const fileSelected = curBkFileSelected;
    if (!fileSelected) return false;
    return (
      fileSelected.findIndex(
        (f, i) => f.isSelected && f.canSelectPage,
      ) > -1
    );
  },
);
